import { useMemo } from 'react'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import { PowerPerformanceForms, UserInvestmentType } from '../../../types/mutual-fund-detail.types'
import { convertEpochToDate } from '../../../utils/utils'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { KnowWhyBottomSheet } from '../../BottomSheetComponents/KnowWhyBottomSheet'
import { KNOW_WHY_REASONS_HEADING_ID } from '../../know-why/constants'
import { fetchKnowWhyData } from '../../../repository/MutualFundDetail'
import { useQuery } from '@tanstack/react-query'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { GROUPED_STATIC_TEXT_ROLE } from '../../ui-components/NameValueTableRow'

type PowerFormConfigDatum = {
  label: string
  sepColor: string
  textColor: string
  mainIcon: string
  pointerIcon: string
  pointers: { pointerKey: string; pointerValue: string }[]
}

const powerFormErrorDatum: PowerFormConfigDatum = {
  label: 'Not Available',
  sepColor: '#F0F4F7',
  textColor: '#6A6E6E',
  mainIcon: `errtrack-icon.svg`,
  pointerIcon: `errtrack-pointer-icon.svg`,
  pointers: [
    {
      pointerKey: 'SIP',
      pointerValue: 'Not Available'
    }
  ]
}

export default function PowerForm({
  powerForm,
  time,
  userInvestmentType,
  isPowerRankAvailable,
  isError
}: {
  powerForm: `${PowerPerformanceForms}`
  time: number
  userInvestmentType: `${UserInvestmentType}`
  isPowerRankAvailable: boolean
  isError?: boolean
}) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { isin } = useMetaDataContext()

  const powerFormConfig: {
    [x in `${PowerPerformanceForms}`]: PowerFormConfigDatum
  } = useMemo(() => {
    return {
      [PowerPerformanceForms.IN_FORM]: {
        label: 'In-form',
        sepColor: '#D7F9E1',
        textColor: '#00661D',
        mainIcon: `inform-icon.svg`,
        pointerIcon: `inform-pointer-icon.svg`,
        pointers: [
          {
            pointerKey: 'SIP_LUMPSUM',
            pointerValue: (() => {
              if (userInvestmentType === UserInvestmentType.SIP) return 'Great to Invest more / Continue SIP'
              return 'Great to Invest / Start SIP'
            })()
          }
        ]
      },
      [PowerPerformanceForms.ON_TRACK]: {
        label: 'On-track',
        sepColor: '#D7F9E1',
        textColor: '#00661D',
        mainIcon: `ontrack-icon.svg`,
        pointerIcon: `ontrack-pointer-icon.svg`,
        pointers: [
          {
            pointerKey: 'SIP_LUMPSUM',
            pointerValue: (() => {
              if (userInvestmentType === UserInvestmentType.SIP) return 'Good to Invest more / Continue SIP'
              return 'Good to Invest / Start SIP'
            })()
          }
        ]
      },
      [PowerPerformanceForms.OFF_TRACK]: {
        label: 'Off-track',
        sepColor: '#FFF3E4',
        textColor: '#995400',
        mainIcon: `offtrack-icon.svg`,
        pointerIcon: `offtrack-pointer-icon.svg`,
        pointers: [
          {
            pointerKey: 'SIP_LUMPSUM',
            pointerValue: (() => {
              if (userInvestmentType === UserInvestmentType.SIP) return 'Hold Investment / Pause SIP'
              return 'Hold Investment / Don’t Start SIP'
            })()
          }
        ]
      },
      [PowerPerformanceForms.OUT_OF_FORM]: {
        label: 'Out-of-form',
        sepColor: '#FFE9E6',
        textColor: '#941D1D',
        mainIcon: `outofform-icon.svg`,
        pointerIcon: `outofform-pointer-icon.svg`,
        pointers: [
          {
            pointerKey: 'SIP_LUMPSUM',
            pointerValue: (() => {
              return 'Exit Now'
            })()
          }
        ]
      },
      [PowerPerformanceForms.NOT_RATED]: powerFormErrorDatum,
      [PowerPerformanceForms.PERFORMANCE_LOCKED]: powerFormErrorDatum
    }
  }, [userInvestmentType])

  const powerFormData = useMemo(() => {
    if (isError) return powerFormErrorDatum
    if (powerForm) return powerFormConfig?.[powerForm]
    else powerFormConfig?.[PowerPerformanceForms.IN_FORM]
  }, [powerForm, powerFormConfig])

  const { data: knowWhyDataResponse } = useQuery({
    queryKey: ['knowWhyData', isin],
    queryFn: () => fetchKnowWhyData(isin ?? ''),
    enabled: !!isin
  })

  const knowWhyData = useMemo(() => knowWhyDataResponse?.data ?? null, [knowWhyDataResponse])

  const handleSeeWhyClick = () => {
    if (knowWhyData) {
      openBottomSheet(
        <KnowWhyBottomSheet onDismiss={closeBottomSheet} ScreenName={'MF_L2'} knowWhyData={knowWhyData} />,
        closeBottomSheet,
        { drawerTitleId: KNOW_WHY_REASONS_HEADING_ID, focusDrawerOnOpen: true }
      )
    }
  }

  const monthLabel = !isError && time ? convertEpochToDate(time, '%b') : ''
  const performanceSummary = `Fund performance, ${powerFormData?.label ?? ''}${monthLabel ? ` in ${monthLabel}` : ''}`

  return (
    <div className='flex flex-col'>
      <div
        className='flex gap-4'
        role={GROUPED_STATIC_TEXT_ROLE}
        aria-label={performanceSummary}
      >
        <div aria-hidden='true' className='h-10 w-10'>
          <img
            src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/${powerFormData?.mainIcon}`}
            alt=''
            className='h-full w-full'
          />
        </div>
        <div aria-hidden='true' className='flex flex-col gap-[3px] pt-[2px]'>
          <div className='text-13 leading-[70%] text-[#6A6E6E]'>Fund Performance</div>
          <div className='text-[22px]'>
            <span className='text-black font-[700]'>{powerFormData?.label}</span>{' '}
            {monthLabel && <span className='text-[#6A6E6E] font-[400]'>in {monthLabel}</span>}
          </div>
        </div>
      </div>
      {
        <div
          aria-hidden='true'
          style={{ borderColor: powerFormConfig?.[powerForm]?.sepColor }}
          className='border-l-2 font-[500] ml-[18px] h-4 mt-[-4px] mb-[2px]'
        />
      }
      <div className='flex flex-col'>
        {powerFormData?.pointers?.map(({ pointerKey, pointerValue }, index) => {
          return (
            <div key={`${pointerKey}${pointerValue}`} className='flex flex-col'>
              {index !== 0 && (
                <div
                  aria-hidden='true'
                  style={{ borderColor: powerFormConfig?.[powerForm]?.sepColor }}
                  className='border-l-2 font-[500] ml-[18px] h-2 my-[3px]'
                />
              )}
              <div className='flex gap-4 items-center'>
                <div aria-hidden='true' className='w-10 flex items-center justify-center'>
                  <div className='h-10 w-10'>
                    <img
                      src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/${powerFormData?.pointerIcon}`}
                      alt=''
                      className='h-full w-full'
                    />
                  </div>
                </div>
                <div className='flex flex-col gap-2'>
                  <div className='font-[500] text-black text-[14px]'>{pointerValue}</div>
                  {isPowerRankAvailable &&
                    knowWhyData &&
                    powerForm !== PowerPerformanceForms.NOT_RATED &&
                    powerForm !== PowerPerformanceForms.PERFORMANCE_LOCKED && (
                      <button
                        type='button'
                        aria-label='See why'
                        onClick={handleSeeWhyClick}
                        className='animate-fade-250 p-0 m-0 border-none bg-transparent cursor-pointer w-max min-h-[24px] flex items-center'
                      >
                        <SeeWhyPointer fill={powerFormConfig?.[powerForm]?.textColor ?? '#941D1D'} />
                      </button>
                    )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const SeeWhyPointer = ({ fill }: { fill: string }) => {
  return (
    <svg
      width='60'
      height='12'
      viewBox='0 0 60 12'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path
        d='M3.835 9.604C2.067 9.604 0.611 8.434 0.351 6.77L1.742 6.341C1.898 7.511 2.795 8.278 3.887 8.278C4.966 8.278 5.668 7.771 5.668 6.991C5.668 4.833 0.689 6.016 0.689 2.74C0.689 1.297 2.028 0.296 3.809 0.296C5.512 0.296 6.851 1.206 7.111 2.571L5.746 3.13C5.564 2.207 4.849 1.622 3.809 1.622C2.821 1.622 2.223 2.038 2.223 2.74C2.223 4.573 7.215 3.299 7.215 6.965C7.215 8.551 5.876 9.604 3.835 9.604ZM8.13627 6.224C8.13627 4.456 9.25427 2.844 11.3343 2.844C13.4403 2.844 14.5583 4.378 14.5583 5.977C14.5583 6.172 14.5453 6.445 14.5323 6.562H9.52727C9.65727 7.693 10.3853 8.395 11.4253 8.395C12.2833 8.395 12.8943 7.992 13.0633 7.277L14.4023 7.615C14.0513 8.876 12.9333 9.604 11.3993 9.604C9.31927 9.604 8.13627 7.992 8.13627 6.224ZM9.59227 5.483H13.0503C12.9593 4.599 12.3743 3.962 11.3343 3.962C10.4373 3.962 9.80027 4.508 9.59227 5.483ZM15.5249 6.224C15.5249 4.456 16.6429 2.844 18.7229 2.844C20.8289 2.844 21.9469 4.378 21.9469 5.977C21.9469 6.172 21.9339 6.445 21.9209 6.562H16.9159C17.0459 7.693 17.7739 8.395 18.8139 8.395C19.6719 8.395 20.2829 7.992 20.4519 7.277L21.7909 7.615C21.4399 8.876 20.3219 9.604 18.7879 9.604C16.7079 9.604 15.5249 7.992 15.5249 6.224ZM16.9809 5.483H20.4389C20.3479 4.599 19.7629 3.962 18.7229 3.962C17.8259 3.962 17.1889 4.508 16.9809 5.483ZM27.8957 9.5L25.8677 2.948H27.3107L28.5457 7.524L29.9627 2.948H31.2757L32.6797 7.524L33.9147 2.948H35.3577L33.3427 9.5H32.0297L30.6127 4.937L29.1957 9.5H27.8957ZM36.5058 9.5V0.296H37.9228V4.079C38.3778 3.26 39.1448 2.844 40.0678 2.844C41.5498 2.844 42.5248 3.767 42.5248 5.197V9.5H41.1078V5.522C41.1078 4.651 40.5618 4.118 39.6778 4.118C38.5858 4.118 37.9228 5.067 37.9228 6.666V9.5H36.5058ZM48.2356 2.948H49.6916L46.2466 11.866H44.7906L45.7786 9.305L43.2306 2.948H44.7646L46.4806 7.485L48.2356 2.948Z'
        fill={fill}
        fillOpacity='0.8'
      />
      <path
        d='M55 7.79998C55 8.08311 55.203 8.33835 55.5144 8.4467C55.8258 8.55504 56.1843 8.49515 56.4226 8.29495L59.756 5.49497C60.0813 5.2216 60.0813 4.7784 59.756 4.50503L56.4226 1.70505C56.1842 1.50485 55.8258 1.44496 55.5144 1.5533C55.203 1.66165 55 1.9169 55 2.20002L55 7.79998Z'
        fill={fill}
        fillOpacity='0.8'
      />
    </svg>
  )
}
